import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '../../../assets/icons/border-edit-pencil.svg';
import { AccountLevelInterface } from '../../../interface/account-level.interface';

interface SidePanelAccountDetailsProperties {
  onClose: () => void;
  data: AccountLevelInterface | null;
  onEdit: () => void;
}

/**
 * Side Panel Account Details
 * @param properties - The component properties
 * @returns JSX Element for the account details side panel
 */
function SidePanelAccountDetails(properties: SidePanelAccountDetailsProperties) {
  const { data, onEdit } = properties;
  const { t, i18n } = useTranslation('sharedComponent');

  if (!data) return null;

  /**
   * Helper to extract fee details
   * @param field - The fee field name ('platformFee' or 'sessionFee')
   * @returns The formatted fee value and type
   */
  const getFeeDisplay = (field: 'platformFee' | 'sessionFee') => {
    let value = '';
    let type = '';

    if (data.settings && data.settings[field] !== undefined) {
      value = String(data.settings[field]);
      type = data.settings[`${field}Type`] === 'amount' ? t('currency') : '%';
    } else if (data[field] !== undefined) {
      value = String(data[field]);
      // Default type or check root level type if available
      const rootType = (data as any)[`${field}Type`];
      type = rootType === 'amount' ? t('currency') : '%';
    }

    return { value, type };
  };

  const platformFee = getFeeDisplay('platformFee');
  const sessionFee = getFeeDisplay('sessionFee');

  return (
    <div className="account-fee" dir={i18n.dir()}>
      <div className="form-header">
        <div className="">
          <h3>{t('accountDetails')}</h3>
        </div>
      </div>

      <div className="form-body">
        <div className="detail-row-display">
          <span className="detail-label">{t('accountId')}</span>
          <div className="detail-value">{data.accountCustomId || '-'}</div>
        </div>

        <div className="detail-row-display">
          <span className="detail-label">{t('accountName')}</span>
          <div className="detail-value">{data.accountName || '-'}</div>
        </div>

        <div className="detail-row">
          <div>
            <span className="detail-label">{t('platformFee')}</span>
            <div className="detail-value">
              {platformFee.value} {platformFee.type}
            </div>
          </div>
          <div className="edit-icon" role="presentation" onClick={onEdit}>
            <EditIcon />
          </div>
        </div>

        <div className="detail-row">
          <div>
            <span className="detail-label">{t('sessionFee')}</span>
            <div className="detail-value">
              {sessionFee.value} {sessionFee.type}
            </div>
          </div>
          <div className="edit-icon" role="presentation" onClick={onEdit}>
            <EditIcon />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SidePanelAccountDetails;
